import React from "react";
import {
  Typography,
  CardActionArea,
  CardMedia,
  Card,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BarChartIcon from "@mui/icons-material/BarChart";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LockIcon from "@mui/icons-material/Lock";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";

const MAP_CARD_ACCENTS_BY_ID = {
  de_nuke: {
    mainColor: "#4ae0d2",
    hoverEffect: "0 0 20px #4ae0d280",
  },
  de_mirage: {
    mainColor: "#d94f3b",
    hoverEffect: "0 0 20px #d94f3b80",
  },
  de_cache: {
    mainColor: "#7f8c8d",
    hoverEffect: "0 0 20px #7f8c8d80",
  },
  de_ancient: {
    mainColor: "#2cb34a",
    hoverEffect: "0 0 20px #2cb34a80",
  },
  de_dust_ii: {
    mainColor: "#f0b36c",
    hoverEffect: "0 0 20px #f0b36c80",
  },
  de_vertigo: {
    mainColor: "#ffe24a",
    hoverEffect: "0 0 20px #ffe24a80",
  },
  de_inferno: {
    mainColor: "#5a8fff",
    hoverEffect: "0 0 20px #5a8fff80",
  },
  de_anubis: {
    mainColor: "#47cc8c",
    hoverEffect: "0 0 20px #47cc8c80",
  },
  de_cobblestone_2018: {
    mainColor: "#8d6e63",
    hoverEffect: "0 0 20px #8d6e6380",
  },
};

const useStyles = makeStyles((theme) => ({
  cardAction:  {
    width: "11.5rem",
    borderRadius: "16px",
    backgroundColor: 'transparent !important',
  },
  cardLayout: (props) => ({
    display: "flex",
    flexDirection: "column",
    position: "relative",
    width: "10.2rem",
    borderRadius: "16px",
    backgroundColor: "#1C1E20",
    color: "white",
    padding: "0.7rem",
    boxShadow: "0 0 12px rgba(0, 0, 0, 0.4)",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    overflow: "visible",
    "&:hover": {
      transform: "scale(1.03)",
      boxShadow: props.hoverEffect,
    },
  }),
  media: {
    width: "100%",
    height: "6rem",
    position: "relative",
    borderRadius: "8px",
  },
  winrateContent: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  winrateText: (props) => ({
    fontSize: "12px",
    color: props.mainColor,
    fontWeight: "bold",
  }),
  containerContent: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  nameMap: {
    textAlign: "start", 
    fontSize: "22px", 
    fontWeight: "550", 
    marginTop: "0.2rem"
  },
  linearRoot: {
    position: "relative",
    width: "100%",
    height: "0.3rem",
    borderRadius: 6,
    backgroundColor: "#797979",
  },
  bar: (props) => ({
    borderRadius: 6,
    backgroundColor: props.mainColor,
  }),
  containerWins: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "0.5rem",
  },
  format: (props) => ({
    display: "flex",
    alignItems: "center",
    color: props.mainColor,
  }),
  winIcon: {
    width: "1.2rem",
    height: "1.2rem",
    marginRight: "0.4rem",
  },
  containerADR: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "0.5rem",
  },
  adrIcon: {
    width: "1rem",
    height: "1rem",
    marginRight: "0.4rem",
  },
  containerLastWins: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: "1rem",
  },
  matchWonLabel: (props) => ({
    fontSize: "12px",
    color: "#ffffff",
    backgroundColor: props.mainColor,
    padding: "0.1rem 0.2rem",
    borderRadius: "4px",
    fontWeight: "550",
  }),
  matchLoss: {
    color: "#ffffff",
    fontWeight: "500",
    fontSize: "12px",
  },
  containerLastMatchIcon: (props) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "2.8rem",
    height: "2.8rem",
    backgroundColor: props.mainColor,
    borderRadius: "8px",
    boxShadow:
      "inset 0 2px 4px rgba(0, 0, 0, 0.6), inset 0 -1px 3px rgba(255, 255, 255, 0.05)",
  }),
  trendingIcon: {
    width: "2.5rem",
    height: "2.5rem",
  },
}));

export default function MapCard({ name, stats }) {
  const mapMeta = getMapMetaById(name);
  const accent = MAP_CARD_ACCENTS_BY_ID[name];
  const displayName = mapMeta?.display || name;
  const mainColor = accent?.mainColor || "#797979";
  const imageUrl = getMapImageUrl(name);
  const hoverEffect = accent?.hoverEffect || "0 0 20px rgba(121, 121, 121, 0.5)";

  const classes = useStyles({ mainColor: mainColor, hoverEffect: hoverEffect });

  const mapStats = stats?.[name];

  if (!mapStats || mapStats.gamesPlayed === 0) return null;

  const lastMapGamesResult = mapStats.resultLastFiveGames || [];
  const lastMatchResult = lastMapGamesResult[lastMapGamesResult.length - 1];

  return (
    <Card className={classes.cardLayout}>
      <div>
        <div>
          <CardMedia
            className={classes.media}
            image={imageUrl || undefined}
            style={{
              backgroundImage: imageUrl ? `url(${imageUrl})` : "none",
              backgroundColor: imageUrl ? "transparent" : "rgba(255, 255, 255, 0.06)",
            }}
            title={name}
          />
        </div>
        <div className={classes.containerContent}>
          <Typography className={classes.nameMap}>
            {displayName}
          </Typography>
          <div>
            <div className={classes.winrateContent}>
              <Typography className={classes.winrateText}>WIN RATE</Typography>
              <Typography variant="body2">{mapStats.winRate}%</Typography>
            </div>

            <LinearProgress
              variant="determinate"
              value={mapStats.winRate}
              classes={{
                root: classes.linearRoot,
                bar: classes.bar,
              }}
            />
          </div>
          <div className={classes.containerWins}>
            <Typography variant="body2" className={classes.format}>
              <BarChartIcon className={classes.winIcon} />
              WIN
            </Typography>
            <Typography variant="body2">{mapStats.gamesWon}</Typography>
          </div>
          <div className={classes.containerADR}>
            <Typography variant="body2" className={classes.format}>
              <GpsFixedIcon className={classes.adrIcon} />
              ADR
            </Typography>
            <Typography variant="body2">{mapStats.avgAdr}</Typography>
          </div>
          <div className={classes.containerLastWins}>
            <div>
              <Typography style={{fontSize: "14px",}}>
                Last matches
              </Typography>
              <Typography style={{ textAlign: "start", }} >
                {lastMapGamesResult?.map((result, index) => (
                  <span
                    key={index}
                    className={
                      result === "W" ? classes.matchWonLabel : classes.matchLoss
                    }
                    style={{ marginRight: "0.25rem" }}
                  >
                    {result}
                  </span>
                ))}
              </Typography>
            </div>
            <div className={classes.containerLastMatchIcon}>
              {lastMatchResult == "W" ? (
                <TrendingUpIcon className={classes.trendingIcon} />
              ) : (
                <TrendingDownIcon className={classes.trendingIcon} />
              )}
            </div>
          </div>
        </div>
      </div>

    </Card>
  );
}